import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Palette, Music, Zap, Frame, Home, Tag, Leaf, Gift, Calendar } from 'lucide-react';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Icon mapping for event types
export const EVENT_ICONS = {
  Palette: Palette,
  Music: Music,
  Zap: Zap,
  Frame: Frame,
  Home: Home,
  Tag: Tag,
  Leaf: Leaf,
  Gift: Gift,
  Calendar: Calendar,
};

export const EVENT_TYPES = {
  craft_show: { label: 'Craft Show', color: 'amber', color_hex: '#F59E0B', icon: 'Palette' },
  festival: { label: 'Festival', color: 'pink', color_hex: '#EC4899', icon: 'Music' },
  comic_con: { label: 'Comic Con', color: 'purple', color_hex: '#A855F7', icon: 'Zap' },
  art_show: { label: 'Art Show', color: 'cyan', color_hex: '#06B6D4', icon: 'Frame' },
  home_show: { label: 'Home Show', color: 'emerald', color_hex: '#10B981', icon: 'Home' },
  flea_market: { label: 'Flea Market', color: 'orange', color_hex: '#F97316', icon: 'Tag' },
  farmers_market: { label: 'Farmers Market', color: 'green', color_hex: '#22C55E', icon: 'Leaf' },
  holiday_market: { label: 'Holiday Market', color: 'red', color_hex: '#EF4444', icon: 'Gift' },
  other: { label: 'Other', color: 'gray', color_hex: '#6B7280', icon: 'Calendar' },
};

// Map a color_hex value to the closest Tailwind color name so consumers that
// still depend on the `color` string (e.g. tailwind classes like badge-<color>)
// keep working even when backend only supplies a hex.
const _HEX_TO_COLOR_NAME = {
  '#F59E0B': 'amber',
  '#EC4899': 'pink',
  '#A855F7': 'purple',
  '#06B6D4': 'cyan',
  '#10B981': 'emerald',
  '#F97316': 'orange',
  '#22C55E': 'green',
  '#EF4444': 'red',
  '#6B7280': 'gray',
};

/**
 * Sync EVENT_TYPES with the backend's canonical registry at
 * GET /api/config/event-types. Mutates the EVENT_TYPES object in place so all
 * existing consumers pick up the new values without needing a rerender/context.
 *
 * Non-fatal: if the backend is unreachable or returns an unexpected shape,
 * the static defaults above remain. The promise always resolves.
 */
export async function syncEventTypesFromBackend() {
  try {
    const { configAPI } = await import('./api');
    const { data } = await configAPI.eventTypes();
    if (!data || typeof data.types !== 'object') return;
    // Replace in-place: keep reference identity, let new keys win.
    Object.keys(EVENT_TYPES).forEach((k) => delete EVENT_TYPES[k]);
    Object.entries(data.types).forEach(([key, val]) => {
      EVENT_TYPES[key] = {
        label: val.label || key,
        color:
          val.color ||
          _HEX_TO_COLOR_NAME[(val.color_hex || '').toUpperCase()] ||
          'gray',
        color_hex: val.color_hex || '#6B7280',
        icon: val.icon || 'Calendar',
      };
    });
  } catch (err) {
    // Backend unavailable — keep static defaults. Don't spam console.
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('syncEventTypesFromBackend skipped:', err?.message);
    }
  }
}

export const EVENT_SIZES = {
  small: { label: 'Small (< 50 vendors)', value: 'small' },
  medium: { label: 'Medium (50-200 vendors)', value: 'medium' },
  large: { label: 'Large (200+ vendors)', value: 'large' },
};

export const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// State abbreviation to full name mapping for backward compatibility
export const STATE_ABBREV_TO_FULL = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

// Normalize state value (convert abbreviation to full name if needed)
export function normalizeState(stateValue) {
  if (!stateValue) return '';
  const trimmed = stateValue.trim();
  // Check if it's an abbreviation (2-3 letters)
  if (trimmed.length <= 3) {
    return STATE_ABBREV_TO_FULL[trimmed.toUpperCase()] || trimmed;
  }
  return trimmed;
}

export function formatDate(dateString) {
  if (!dateString) return '';
  // Parse as local date to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Parse a date string (YYYY-MM-DD) as a local date (not UTC)
export function parseLocalDate(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Format a date range (e.g., "Jan 1 - Jan 3, 2025" or "Jan 1, 2025" if same day)
export function formatDateRange(startDate, endDate) {
  if (!startDate) return '';
  // Parse as local date to avoid timezone issues
  const start = parseLocalDate(startDate);
  const end = endDate ? parseLocalDate(endDate) : null;
  
  const startFormatted = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  
  if (!end || start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  
  // If same year, don't repeat the year
  if (start.getFullYear() === end.getFullYear()) {
    const endFormatted = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startFormatted} - ${endFormatted}`;
  }
  
  // Different years, show both
  const startFull = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const endFull = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startFull} - ${endFull}`;
}

// Format all event dates including primary and additional dates
export function formatEventDates(event) {
  if (!event) return '';
  
  const dates = [];
  
  // Add primary date range
  if (event.date) {
    dates.push(formatDateRange(event.date, event.end_date));
  }
  
  // Add additional dates
  if (event.event_dates && Array.isArray(event.event_dates)) {
    event.event_dates.forEach(d => {
      if (d.start_date) {
        dates.push(formatDateRange(d.start_date, d.end_date));
      }
    });
  }
  
  return dates;
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return 'TBD';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getDaysUntil(dateString) {
  if (!dateString) return null;
  // Parse as local date to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Compare dates only
  const diffTime = date - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function isDeadlineSoon(deadlineString) {
  const days = getDaysUntil(deadlineString);
  return days !== null && days >= 0 && days <= 14;
}

/**
 * Check if an event is expired (deadline has passed or event date has passed)
 * For filtering out past events in the UI
 */
export function isEventExpired(event) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Check application deadline first (if exists)
  if (event.deadline) {
    const [year, month, day] = event.deadline.split('-').map(Number);
    const deadlineDate = new Date(year, month - 1, day);
    if (deadlineDate < today) return true;
  }
  
  // If no deadline, check event end date (or start date if no end date)
  const eventDateStr = event.end_date || event.date;
  if (eventDateStr) {
    const [year, month, day] = eventDateStr.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);
    if (eventDate < today) return true;
  }
  
  return false;
}
